<template>
  <div class="order-detail-page app-page">

    <el-card v-loading="loading" class="detail-card">
      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-if="orderDetail && !error" class="order-detail-content">
        <!-- 订单状态横幅 -->
        <div class="status-banner" :class="'status-' + orderDetail.status">
          <div class="status-icon">
            <template v-if="orderDetail.status === 0">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </template>
            <template v-else-if="orderDetail.status === 1">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 12l2 2 4-4"/></svg>
            </template>
            <template v-else>
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </template>
          </div>
          <div class="status-text">
            <span class="status-label">{{ getStatusText(orderDetail.status) }}</span>
            <span class="status-desc" v-if="orderDetail.status === 0">等待您完成支付</span>
            <span class="status-desc" v-else-if="orderDetail.status === 1">支付已完成</span>
            <span class="status-desc" v-else>订单已取消</span>
          </div>
        </div>

        <!-- 商品信息 -->
        <div class="detail-section">
          <div class="section-title-wrap">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
            <h3 class="section-title">商品信息</h3>
          </div>
          <div class="product-list">
            <div class="product-item" v-for="(item, index) in orderDetail.items" :key="index">
              <el-image
                :src="item.mainImage"
                alt="商品图片"
                class="product-img"
              ></el-image>
              <div class="product-details">
                <h4 class="product-name">{{ item.productName }}</h4>
                <div class="product-meta">
                  <span>单价：¥{{ item.price.toFixed(2) }}</span>
                  <span class="meta-dot"></span>
                  <span>数量：{{ item.count }}</span>
                </div>
                <div class="product-subtotal">小计：¥{{ item.totalAmount.toFixed(2) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 地址信息 -->
        <div class="detail-section">
          <div class="section-title-wrap">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <h3 class="section-title">收货地址</h3>
          </div>
          <div class="address-card">
            <div class="address-row">
              <span class="addr-label">收货人</span>
              <span class="addr-value">{{ orderDetail.receiver || '未填写' }}</span>
            </div>
            <div class="address-row">
              <span class="addr-label">联系电话</span>
              <span class="addr-value">{{ orderDetail.phone || '未填写' }}</span>
            </div>
            <div class="address-row">
              <span class="addr-label">详细地址</span>
              <span class="addr-value">{{ orderDetail.address || '未填写地址' }}</span>
            </div>
          </div>
        </div>

        <!-- 订单信息 -->
        <div class="detail-section">
          <div class="section-title-wrap">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
            <h3 class="section-title">订单信息</h3>
          </div>
          <div class="info-card">
            <div class="info-row">
              <span class="label">订单编号</span>
              <span class="value mono">{{ orderDetail.orderSn }}</span>
            </div>
            <div class="info-row">
              <span class="label">创建时间</span>
              <span class="value">{{ formatDate(orderDetail.createTime) }}</span>
            </div>
            <div class="info-row">
              <span class="label">订单状态</span>
              <span class="value">
                <el-tag :type="getStatusTagType(orderDetail.status)" effect="dark" class="detail-status-tag">
                  {{ getStatusText(orderDetail.status) }}
                </el-tag>
              </span>
            </div>
            <div class="info-row" v-if="orderDetail.payTime">
              <span class="label">支付时间</span>
              <span class="value">{{ formatDate(orderDetail.payTime) }}</span>
            </div>
            <div class="info-row">
              <span class="label">是否秒杀</span>
              <span class="value">{{ orderDetail.isSeckill === 1 ? '是' : '否' }}</span>
            </div>
            <div class="info-row">
              <span class="label">礼物订单</span>
              <span class="value">{{ orderDetail.isGift == true ? '是' : '否' }}</span>
            </div>
          </div>
        </div>

        <!-- 订单金额 -->
        <div class="amount-section">
          <div class="amount-row">
            <span>订单总金额</span>
            <span class="total-value">¥{{ orderDetail.totalAmount.toFixed(2) }}</span>
          </div>
        </div>

        <!-- 订单操作按钮 -->
        <div class="order-actions" v-if="orderDetail.status === 0">
          <el-button
            type="primary"
            @click="handlePay"
            size="large"
            class="action-btn primary"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            立即支付
          </el-button>
          <el-button
            type="danger"
            @click="handleCancelOrder"
            size="large"
            class="action-btn danger"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            取消订单
          </el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
// 脚本逻辑完全不变，仅样式改造
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import orderApi from '@/api/order'

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const error = ref('')
const orderDetail = ref(null)
const orderSn = ref(route.params.orderSn || '')

const sleep = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusText = (status) => {
  const statusMap = {
    0: '未支付',
    1: '已支付',
    2: '已取消'
  }
  return statusMap[status] || '未知状态'
}

const getStatusTagType = (status) => {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'danger'
  }
  return typeMap[status] || 'default'
}

const fetchOrderDetail = async () => {
  if (!orderSn.value) {
    error.value = '订单编号不存在'
    loading.value = false
    return
  }

  try {
    loading.value = true
    await sleep(1000)

    const res = await orderApi.getOrderDetail(orderSn.value)
    if (res.data.code === '200' || res.data.code === 200) {
      orderDetail.value = res.data.data
      if (!orderDetail.value.items || orderDetail.value.items.length === 0) {
        error.value = '订单商品信息为空'
      }
    } else {
      error.value = res.message || '获取订单详情失败'
      ElMessage.error(error.value)
    }
  } catch (err) {
    console.error('获取订单详情失败:', err)
    error.value = '获取订单详情失败，请稍后重试'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

const handlePay = () => {
  router.push(`/order/pay/${orderSn.value}`)
}

const handleCancelOrder = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要取消该订单吗？',
      '确认取消',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    const res = await orderApi.cancelOrder(orderSn.value)
    if (res.data.code == 200) {
      ElMessage.success('订单已取消')
      fetchOrderDetail()
    } else {
      ElMessage.error(res.message || '取消订单失败')
    }
  } catch (err) {
    if (err === 'cancel') return
    console.error('取消订单失败:', err)
    ElMessage.error('取消订单失败')
  }
}


onMounted(() => {
  fetchOrderDetail()
})
</script>

<style scoped>
.order-detail-page {
  max-width: 900px;
  margin: 0 auto;
}

.detail-card {
  margin-top: 0;
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  overflow: hidden;
}

.order-detail-content {
  padding: 24px;
}

/* Status banner */
.status-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: var(--app-radius-lg);
  margin-bottom: 24px;
  color: #fff;
}

.status-banner.status-0 {
  background: linear-gradient(135deg, #f39c12, #f1c40f);
}

.status-banner.status-1 {
  background: linear-gradient(135deg, #27ae60, #2ecc71);
}

.status-banner.status-2 {
  background: linear-gradient(135deg, #95a5a6, #bdc3c7);
}

.status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
}

.status-text {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 1.25rem;
  font-weight: 700;
}

.status-desc {
  font-size: 0.8125rem;
  opacity: 0.85;
  margin-top: 2px;
}

/* Sections */
.detail-section {
  margin-bottom: 24px;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--app-border-light);
}

.section-title-wrap svg {
  color: var(--app-color-primary);
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

/* Product list */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-lg);
  background: var(--app-bg-soft);
  transition: all 0.2s ease;
}

.product-item:hover {
  border-color: var(--app-color-primary-light);
}

.product-img {
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-light);
  flex-shrink: 0;
}

.product-details {
  flex: 1;
}

.product-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0 0 6px;
}

.product-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  margin-bottom: 4px;
}

.meta-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--app-text-muted);
}

.product-subtotal {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--app-color-primary);
}

/* Address card */
.address-card {
  padding: 16px 20px;
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-lg);
  background: var(--app-bg-soft);
}

.address-row {
  display: flex;
  padding: 8px 0;
  font-size: 0.9375rem;
}

.address-row + .address-row {
  border-top: 1px dashed var(--app-border-light);
}

.addr-label {
  width: 90px;
  color: var(--app-text-secondary);
  font-weight: 500;
  flex-shrink: 0;
}

.addr-value {
  color: var(--app-text-primary);
  flex: 1;
}

/* Info card */
.info-card {
  padding: 4px 20px;
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-lg);
  background: var(--app-bg-soft);
}

.info-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  font-size: 0.9375rem;
}

.info-row + .info-row {
  border-top: 1px dashed var(--app-border-light);
}

.info-row .label {
  width: 90px;
  color: var(--app-text-secondary);
  font-weight: 500;
  flex-shrink: 0;
}

.info-row .value {
  flex: 1;
  color: var(--app-text-primary);
}

.info-row .value.mono {
  font-family: 'SF Mono', 'Courier New', monospace;
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
}

.detail-status-tag {
  border-radius: var(--app-radius-full) !important;
  font-weight: 500 !important;
  border: none !important;
}

/* Amount section */
.amount-section {
  padding: 16px 20px;
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-lg);
  background: linear-gradient(135deg, var(--app-bg-soft), var(--app-color-primary-muted));
  margin-bottom: 24px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  color: var(--app-text-secondary);
}

.total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--app-color-primary);
}

/* Action buttons */
.order-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 8px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px !important;
  font-weight: 600 !important;
  border-radius: var(--app-radius-lg) !important;
}

.action-btn.primary {
  background: var(--app-gradient-primary) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(255, 126, 126, 0.35) !important;
}

.action-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.5) !important;
}

.action-btn.danger {
  border: 1px solid var(--app-border-light) !important;
  background: var(--app-bg-elevated) !important;
  color: var(--app-text-secondary) !important;
}

.action-btn.danger:hover {
  border-color: #f56c6c !important;
  color: #f56c6c !important;
  background: rgba(245, 108, 108, 0.05) !important;
}

.error-message {
  color: #f56c6c;
  text-align: center;
  padding: 40px 20px;
}

@media (max-width: 768px) {
  .order-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-row .label {
    width: auto;
  }
}
</style>
